import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import { EmptyState, LoadingOverlay } from 'dashboard/components/common';
import PlanDialog from './PlanDialog';

/**
 * Subscription plans, managed without opening the Stripe dashboard.
 *
 * The screen is shaped by what Stripe will and will not allow, and says so
 * rather than hiding it: prices cannot be edited (repricing makes a new one),
 * plans cannot be deleted (they archive), and neither action moves or cancels
 * anybody who is already subscribed.
 *
 * Archived plans stay listed, dimmed. An admin looking for a plan they
 * retired last month needs to find it — to see what it cost, or bring it
 * back — and a list that silently drops things is a list you cannot trust.
 */

const money = (amount, currency) => {
  if (amount === null || amount === undefined) return '—';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'EUR' })
      .format(amount);
  } catch (_) {
    return `${amount} ${currency || ''}`.trim();
  }
};

const EVERY = { month: 'month', year: 'year', week: 'week', day: 'day' };

function PlanRow({ plan, onEdit, onToggleActive }) {
  const price = plan.current_price;
  return (
    <Stack
      direction='row'
      alignItems='center'
      justifyContent='space-between'
      gap='12px'
      // Wraps rather than squashing: a plan name and a price fighting for a
      // 390px row makes both unreadable, and the price is the number people
      // are checking.
      flexWrap='wrap'
      sx={{
        p: '14px 16px',
        borderRadius: '12px',
        border: '1px solid',
        borderColor: 'divider',
        opacity: plan.active ? 1 : 0.55,
      }}
    >
      <Box minWidth={0} flex='1 1 200px'>
        <Stack direction='row' alignItems='center' spacing='8px' flexWrap='wrap'>
          <Typography fontWeight='700'>{plan.name}</Typography>
          {!plan.active && <Chip size='small' variant='outlined' label='Archived' />}
          {plan.old_prices?.length > 0 && (
            <Tooltip title='Older prices still bill the customers who signed up on them'>
              <Chip size='small' variant='outlined' label={`${plan.old_prices.length} old price${plan.old_prices.length === 1 ? '' : 's'}`} />
            </Tooltip>
          )}
        </Stack>
        {plan.description && (
          <Typography variant='caption' color='text.secondary' display='block' mt='2px'>
            {plan.description}
          </Typography>
        )}
      </Box>

      <Stack direction='row' alignItems='center' spacing='12px' flexShrink={0}>
        <Box textAlign='right'>
          <Typography fontWeight='700' sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {price ? money(price.amount, price.currency) : 'No price'}
          </Typography>
          {price && (
            <Typography variant='caption' color='text.secondary'>
              per {EVERY[price.interval] || price.interval}
            </Typography>
          )}
        </Box>
        <Tooltip title='Edit'>
          <IconButton size='small' onClick={() => onEdit(plan)}>
            <EditIcon fontSize='small' />
          </IconButton>
        </Tooltip>
        <Tooltip title={plan.active ? 'Stop offering this plan' : 'Offer this plan again'}>
          <IconButton size='small' onClick={() => onToggleActive(plan)}>
            {plan.active ? <ArchiveIcon fontSize='small' /> : <UnarchiveIcon fontSize='small' />}
          </IconButton>
        </Tooltip>
      </Stack>
    </Stack>
  );
}

export default function PlansTab() {
  const [plans, setPlans] = useState([]);
  const [stripeOn, setStripeOn] = useState(true);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState({ open: false, plan: null });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    API.adminListPlans({})
      .then((res) => { setPlans(res?.items || []); setStripeOn(res?.stripe !== false); })
      .catch((err) => toast.error(err?.message || 'Could not load plans'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = (form) => {
    setBusy(true);
    const done = (msg) => { toast.success(msg); setDialog({ open: false, plan: null }); load(); };
    const fail = (err) => toast.error(err?.message || 'Failed');

    if (!form.product_id) {
      API.adminCreatePlan(form)
        .then(() => done('Plan created'))
        .catch(fail).finally(() => setBusy(false));
      return;
    }

    // Name and description first; repricing is a second call because Stripe
    // models it as creating a different object, not editing this one.
    API.adminUpdatePlan({ product_id: form.product_id, name: form.name, description: form.description })
      .then(() => (form.reprice
        ? API.adminChangePlanPrice({
          product_id: form.product_id,
          amount: form.amount,
          currency: form.currency,
          interval: form.interval,
        }).then(() => done('Plan updated and repriced for new customers'))
        : done('Plan updated')))
      .catch(fail)
      .finally(() => setBusy(false));
  };

  const toggleActive = (plan) => {
    const next = !plan.active;
    const warning = next
      ? `Offer “${plan.name}” again?`
      : `Stop offering “${plan.name}”?\n\nNobody currently subscribed is cancelled or charged differently — it just disappears from checkout.`;
    if (!window.confirm(warning)) return;
    API.adminSetPlanActive({ product_id: plan.id, active: next })
      .then(() => { toast.success(next ? 'Plan is available again' : 'Plan archived'); load(); })
      .catch((err) => toast.error(err?.message || 'Failed'));
  };

  return (
    <Box>
      <Stack direction='row' alignItems='center' justifyContent='space-between' mb='14px' gap='12px' flexWrap='wrap'>
        <Box>
          <Typography variant='h6' fontWeight='700'>Subscription plans</Typography>
          <Typography variant='body2' color='text.secondary'>
            What brands can subscribe to. Changes apply in Stripe immediately.
          </Typography>
        </Box>
        <Button
          variant='contained'
          startIcon={<AddIcon />}
          onClick={() => setDialog({ open: true, plan: null })}
          disabled={!stripeOn}
        >
          New plan
        </Button>
      </Stack>

      {!stripeOn && (
        <Alert severity='warning' sx={{ borderRadius: '10px', mb: '14px' }}>
          Stripe is not configured on this server, so plans cannot be listed or changed.
        </Alert>
      )}

      <LoadingOverlay loading={loading && plans.length === 0} minHeight={200}>
        {stripeOn && plans.length === 0 && !loading && (
          <EmptyState>No plans yet. Create the first one brands can subscribe to.</EmptyState>
        )}
        <Stack spacing='8px'>
          {plans.map((p) => (
            <PlanRow
              key={p.id}
              plan={p}
              onEdit={(plan) => setDialog({ open: true, plan })}
              onToggleActive={toggleActive}
            />
          ))}
        </Stack>
      </LoadingOverlay>

      <PlanDialog
        open={dialog.open}
        plan={dialog.plan}
        busy={busy}
        onClose={() => setDialog({ open: false, plan: null })}
        onSubmit={submit}
      />
    </Box>
  );
}
